/**
 * Air request advanced rule validator class, handles rule evaluation, travel booking validation,
 * merging default and restrictions etc
 */
export default class AirAdvancedRulesValidator {
  constructor({ defaultSchedule = {}, restrictions = {}, rules = [] },
    currentAdmissionItemId, currentRegistrationTypeId) {
    this.currentAdmissionItemId = currentAdmissionItemId;
    this.restrictions = restrictions;
    this.defaultSchedule = defaultSchedule;
    this.advancedRules = rules.filter(ar => ar.isActive && ar.admissionItem.length > 0
      && ar.contactType.length > 0) || [];
    this.currentRegistrationTypeId = currentRegistrationTypeId;
    this.getValidationResult = this.getValidationResult.bind(this);
    this.verifyBooking = this.isAirBookingInvalid.bind(this);
    this.getMatchingAdvancedRule = this.getMatchingAdvancedRule.bind(this);
    this.getMergedRestrictions = this.getMergedRestrictions.bind(this);
    this.getMergedDefaults = this.getMergedDefaults.bind(this);
  }

  getValidationResult(travelBookings) {
    if (this.advancedRules.length === 0 || !travelBookings || travelBookings.length === 0) {
      return { isValid: true };
    }
    // Get matching advanced rule
    const matchingAdvancedRule = this.getMatchingAdvancedRule();

    // Exit early if no rules match
    if (!matchingAdvancedRule) {
      return { isValid: true };
    }

    // Get merged restrictions
    const mergedRestrictions = this.getMergedRestrictions(matchingAdvancedRule);

    // Verify each air booking within the travel bookings for the given merged restrictions
    let invalidAirBookings = [];
    travelBookings.forEach(b => {
      b.airBookings.forEach(ab => {
        if (this.isAirBookingInvalid(mergedRestrictions, ab)) {
          invalidAirBookings.push(ab);
        }
      });
    });
    return invalidAirBookings.length > 0 ? { isValid: false, invalidAirBookings } : { isValid: true };
  }

  isAirBookingInvalid({ earliestDepartureDate, latestReturnDate }, airBooking) {
    const earliestDepartureDateObject = earliestDepartureDate ? new Date(earliestDepartureDate) : null;
    const latestReturnDateObject = latestReturnDate ? new Date(latestReturnDate) : null;
    const departureDateObject = airBooking.departureDate ? new Date(airBooking.departureDate) : null;
    const returnDateObject = airBooking.returnDate ? new Date(airBooking.returnDate) : null;
    const isDepartureDateInValid = this.isDateOutsideBounds(departureDateObject, earliestDepartureDateObject,
      latestReturnDateObject);
    const isReturnDateInValid = this.isDateOutsideBounds(returnDateObject, earliestDepartureDateObject,
      latestReturnDateObject);
    return isDepartureDateInValid || isReturnDateInValid;
  }

  isDateOutsideBounds(givenDate, lowerBoundDate, upperBoundDate) {
    return givenDate &&
        ((lowerBoundDate && givenDate < lowerBoundDate) ||
        (upperBoundDate && givenDate > upperBoundDate));
  }

  getMatchingAdvancedRule() {
    return this.advancedRules.find(rule => rule.admissionItem.includes(this.currentAdmissionItemId)
      && rule.contactType.includes(this.currentRegistrationTypeId));
  }

  getMergedRestrictions(ruleToMerge) {
    const advancedRule = (ruleToMerge || this.getMatchingAdvancedRule()) || {};

    // This logic is in accordance with how the backend processes the merge, if at any point in time
    // the backend changes, this would need to change as well
    let mergedEarliestDepartureDate = this.restrictions.earliestDepartureDate;
    let mergedLatestReturnDate = this.restrictions.latestReturnDate;

    if (!mergedEarliestDepartureDate || (advancedRule.earliestDepartureDate
      && new Date(advancedRule.earliestDepartureDate) > new Date(this.restrictions.earliestDepartureDate))) {
      mergedEarliestDepartureDate = advancedRule.earliestDepartureDate;
    }
    if (!mergedLatestReturnDate || (advancedRule.latestReturnDate
      && new Date(advancedRule.latestReturnDate) < new Date(this.restrictions.latestReturnDate))) {
      mergedLatestReturnDate = advancedRule.latestReturnDate;
    }
    return {
      earliestDepartureDate: mergedEarliestDepartureDate,
      latestReturnDate: mergedLatestReturnDate
    };
  }

  getMergedDefaults() {
    const advancedRule = this.getMatchingAdvancedRule() || {};
    return {
      defaultDepartureDate: advancedRule.defaultDepartureDate || this.defaultSchedule.departureDate,
      defaultReturnDate: advancedRule.defaultReturnDate || this.defaultSchedule.returnDate
    };
  }
}
